import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { useGetBuildingsByIdQuery, useGetParkingSpotsQuery, useGetSpacesByIdQuery, useGetStatisticsVacancyQuery } from '@client/api';
import {
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardProps,
  CardTitle,
  EmptyPlaceholder,
  isDefinedString,
  Pagination,
  Table,
  TableBody,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  TableRow,
  TableCell,
  Text,
  Typography,
  useAreaMetricString,
} from '@client/toolkit';

import { ParkingSpotsListItem } from './parking-spots-list-item.component';

export type ParkingSpotsListCardProps = {
  className?: string;
  variant?: CardProps['variant'];
};

export function ParkingSpotsListCard({ className, variant }: ParkingSpotsListCardProps) {
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');

  const { buildingId, spaceId } = useParams<{ buildingId: string; spaceId: string }>();

  const pageSize = 20;
  const [page, setPage] = useState(0);
  const pageOffset = page * pageSize;

  const {
    data: space,
    isFetching: isFetchingSpace,
    isLoading: isLoadingSpace,
  } = useGetSpacesByIdQuery(
    {
      id: spaceId ?? '',
    },
    { skip: !spaceId },
  );

  const {
    data: building,
    isFetching: isFetchingBuilding,
    isLoading: isLoadingBuilding,
  } = useGetBuildingsByIdQuery(
    {
      id: buildingId ?? '',
    },
    { skip: !buildingId },
  );

  const ancestorPath = useMemo(() => space?.path || building?.path, [building?.path, space?.path]);

  const {
    data: parkingSpotsData,
    isFetching: isFetchingParkingSpots,
    isLoading: isLoadingParkingSpots,
  } = useGetParkingSpotsQuery(
    {
      'ancestorPath.Equal': ancestorPath,
      'pagination.Limit': pageSize,
      'pagination.Offset': pageOffset,
    },
    { skip: !ancestorPath },
  );

  const isLoading = isLoadingBuilding || isLoadingSpace || isLoadingParkingSpots;
  const isFetching = isFetchingBuilding || isFetchingSpace || isFetchingParkingSpots;

  const parkingSpots = useMemo(() => parkingSpotsData?.items ?? [], [parkingSpotsData]);
  const hasItems = useMemo(() => parkingSpots.length > 0, [parkingSpots]);

  const totalCount = parkingSpotsData?.totalCount ?? 0;

  const { data: vacancyReport, isFetching: isFetchingStatistics } = useGetStatisticsVacancyQuery(
    {
      'ancestorPath.Equal': ancestorPath,
      'discriminator.Contains': ['ParkingSpot'],
    },
    { skip: !ancestorPath },
  );

  const totalArea = useAreaMetricString(vacancyReport?.total?.area);

  return (
    <Card className={className} variant={variant}>
      <CardHeader>
        <div className="flex-1">
          <CardTitle variant="dense">{tGlobal('entity.parkingSpot', { context: 'plural' })}</CardTitle>
          <Typography className="mt-2 -mb-1" variant="sub">
            {tCommon('totalEntity', { entity: tGlobal('parkingSpot.areaLower') })}
            <Text
              className="ml-1.5 font-bold text-white"
              isEmpty={!isDefinedString(totalArea)}
              loading={isFetchingStatistics || isFetchingBuilding || isFetchingSpace}
              SkeletonProps={{ className: 'w-[46px]', inline: true }}
            >
              {totalArea}
            </Text>
          </Typography>
        </div>
        <CardActions className="min-h-[32px]" />
      </CardHeader>
      <CardContent variant="fill">
        <Table className="rounded-md border border-border">
          <TableHead>
            <TableHeadRow>
              <TableHeadCell>{tGlobal('space.areaName')}</TableHeadCell>
              <TableHeadCell className="w-[140px]">{tGlobal('parkingSpot.type')}</TableHeadCell>
              <TableHeadCell className="w-[150px]">{tGlobal('parkingSpot.area')}</TableHeadCell>
            </TableHeadRow>
          </TableHead>

          <TableBody>
            {isLoading &&
              Array.from(Array(3).keys()).map((x) => <ParkingSpotsListItem key={x} isFetching />)}

            {!isLoading && !hasItems && (
              <TableRow>
                <TableCell colSpan={3} className="p-4">
                  <EmptyPlaceholder fullWidth>
                    {tCommon('noEntityFound', {
                      entity: tGlobal('entity.parkingSpot', { context: 'plural' }).toLowerCase(),
                    })}
                  </EmptyPlaceholder>
                </TableCell>
              </TableRow>
            )}

            {!isLoading &&
              parkingSpots?.map((parkingSpot) => (
                <ParkingSpotsListItem key={parkingSpot.id} parkingSpot={parkingSpot} isFetching={isFetching} />
              ))}
          </TableBody>
        </Table>

        <Pagination totalCount={totalCount} currentPage={page} setPage={setPage} pageSize={pageSize} />
      </CardContent>
    </Card>
  );
}
